import { PaxCheckQuestion } from "api";
import ChecklistItem, {
  DocumentRequirement,
} from "models/checklist/checklist-item/checklist-item";
import ChecklistItemStatus from "models/checklist/checklist-item/checklist-item-status";

/*
Why the passenger ended up on a visa page. Drives the wording of the
explanatory banner, so that a passenger who does not actually need a visa can
recognise the answer that put them here and go back to change it.
*/
export enum VisaRequirementReason {
  // the passenger ticked a visa in the "which documents do you have" question
  DeclaredVisa = "declared-visa",
  // the trip is longer than the visa-free stay the destination allows
  TripLength = "trip-length",
  // anything else - we can't tell which answer caused it
  Generic = "generic",
}

// The only question ids the backend keeps stable; every other one is an MD5 of
// the rule that raised it. See ChecklistApi.Question.PickFromListQuestion.TYPE.
const ONWARD_FLIGHT_QUESTION_ID = "HasOnwardFlightAtMostAfterArrival";

// doc_category_order of the "visa and exemptions" group - visas, eVisas, ETAs,
// border crossing cards, approval letters, exemption certificates. See
// DocumentCategory.getApiDocCategoryOrder().
const VISA_DOC_CATEGORY_ORDER = 3;

/*
Item types are document types optionally suffixed with a territory, eg "VISA",
"EMBASSY_VISA", "VISA_OR_EVISA-VNM", "IND_EVISA".
*/
function isVisaType(type: string): boolean {
  return type
    .toUpperCase()
    .split(/[-_]/)
    .some((token) => token === "VISA" || token === "EVISA");
}

/*
A "have document" question carries no document type to compare with the item it
raised. What it does carry is a country, built by the same backend function as
the allowed document's, and a category order, built from the same document
category. Together they say the passenger ticked a visa or exemption for the
country this item is about.
*/
function isDeclaredByPax(
  item: DocumentRequirement,
  questions: Array<PaxCheckQuestion>
): boolean {
  const itemCountries = new Set(
    item.allowedDocumentsSet.allowedDocuments
      .map((doc) => doc.country)
      .filter((country): country is string => !!country)
  );

  return questions.some(
    (question) =>
      question.type === "have-document-question" &&
      question.answer === true &&
      question.docCategoryOrder === VISA_DOC_CATEGORY_ORDER &&
      !!question.country &&
      itemCountries.has(question.country)
  );
}

/*
Two shapes carry this: several visa-free windows collapse into one
pick-from-list-question with the stable id, one window stays a plain
declaration-item whose id is a hash but whose kind is set to the same stable
value. A declaration with no kind is some other declaration entirely.
*/
function isOnwardFlightQuestion(question: PaxCheckQuestion): boolean {
  if (question.type === "pick-from-list-question") {
    return question.id === ONWARD_FLIGHT_QUESTION_ID;
  }
  if (question.type === "declaration-item") {
    return question.kind === ONWARD_FLIGHT_QUESTION_ID;
  }
  return false;
}

function exceedsVisaFreeStay(questions: Array<PaxCheckQuestion>): boolean {
  return questions.some(
    (question) =>
      isOnwardFlightQuestion(question) &&
      question.isRelevant &&
      question.answer !== undefined &&
      question.answer !== null
  );
}

/*
Returns null when the item is not an unresolved visa requirement - there is
nothing to explain then.
*/
export function getVisaRequirementReason(
  item: ChecklistItem,
  questions: Array<PaxCheckQuestion>
): VisaRequirementReason | null {
  if (!(item instanceof DocumentRequirement)) {
    return null;
  }

  if (item.status === ChecklistItemStatus.Resolved) {
    return null;
  }

  const isVisaItem =
    isVisaType(item.itemType) ||
    item.allowedDocumentsSet.allowedDocuments.some((doc) =>
      isVisaType(doc.type)
    );

  if (!isVisaItem) {
    return null;
  }

  if (isDeclaredByPax(item, questions)) {
    return VisaRequirementReason.DeclaredVisa;
  }

  if (exceedsVisaFreeStay(questions)) {
    return VisaRequirementReason.TripLength;
  }

  return VisaRequirementReason.Generic;
}
